import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Link,
} from "@mui/material";
import { FileText } from "lucide-react";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";

interface TermsAndConditionsModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
  loading?: boolean;
}

/**
 * TermsAndConditionsModal Component
 * Displays the full terms and conditions for user review
 */
export const TermsAndConditionsModal: React.FC<
  TermsAndConditionsModalProps
> = ({ open, onClose, onAccept, onReject, loading = false }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          fontFamily: CLINICAL_FONT_FAMILY,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontFamily: CLINICAL_FONT_FAMILY,
          fontWeight: 600,
          color: "#0F172A",
          pb: 1,
        }}
      >
        <FileText size={24} color="#0B5FFF" />
        Terms and Conditions
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Introduction */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              Welcome to AIClinTrack
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              These Terms and Conditions ("Terms") govern your access to and use
              of the AIClinTrack clinical trial management platform
              ("Platform"). By registering for an account and using the
              Platform, you agree to be bound by these Terms.
            </Typography>
          </Box>

          {/* Definitions */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              1. Definitions
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#64748B",
                  lineHeight: 1.6,
                  mb: 1,
                }}
              >
                <strong>"User"</strong> means any individual who registers for
                and uses the Platform.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#64748B",
                  lineHeight: 1.6,
                  mb: 1,
                }}
              >
                <strong>"Account"</strong> means the user account created upon
                registration.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#64748B",
                  lineHeight: 1.6,
                }}
              >
                <strong>"Content"</strong> means any data, information, or
                materials uploaded to or generated within the Platform.
              </Typography>
            </Box>
          </Box>

          {/* User Responsibilities */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              2. User Responsibilities
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#64748B",
                  lineHeight: 1.6,
                }}
              >
                You agree to:
              </Typography>
              <ul
                style={{
                  margin: "8px 0",
                  paddingLeft: "20px",
                  fontFamily: CLINICAL_FONT_FAMILY,
                }}
              >
                <li>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#64748B",
                    }}
                  >
                    Maintain the confidentiality of your login credentials
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#64748B",
                    }}
                  >
                    Use the Platform only for authorized clinical trial
                    management purposes
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#64748B",
                    }}
                  >
                    Comply with all applicable laws and regulations
                  </Typography>
                </li>
                <li>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#64748B",
                    }}
                  >
                    Not engage in any unlawful or prohibited activities
                  </Typography>
                </li>
              </ul>
            </Box>
          </Box>

          {/* Data Protection */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              3. Data Protection and Privacy
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              All personal and clinical data processed through the Platform is
              handled in accordance with applicable data protection regulations
              including GDPR, HIPAA, and other privacy laws. You agree that your
              data will be processed as outlined in our
            </Typography>
            <Link
              href="#"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#0B5FFF",
                textDecoration: "none",
                fontSize: "0.875rem",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Privacy Policy
            </Link>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              .
            </Typography>
          </Box>

          {/* Intellectual Property */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              4. Intellectual Property
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              The Platform and all its contents are protected by copyright and
              other intellectual property laws. You retain ownership of content
              you upload but grant us a license to use it for platform
              operations.
            </Typography>
          </Box>

          {/* Limitation of Liability */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              5. Limitation of Liability
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              To the fullest extent permitted by law, we shall not be liable for
              any indirect, incidental, special, or consequential damages
              arising from your use of the Platform.
            </Typography>
          </Box>

          {/* Termination */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              6. Termination
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              We reserve the right to terminate or suspend your account at any
              time if you violate these Terms or for any other reason at our
              sole discretion.
            </Typography>
          </Box>

          {/* Changes to Terms */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                fontWeight: 600,
                color: "#0F172A",
                mb: 1,
              }}
            >
              7. Changes to Terms
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              We may update these Terms at any time. Your continued use of the
              Platform after any changes constitutes your acceptance of the new
              Terms.
            </Typography>
          </Box>

          {/* Contact */}
          <Box sx={{ backgroundColor: "#F0F9FF", p: 2, borderRadius: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#0F172A",
                fontWeight: 500,
              }}
            >
              If you have any questions about these Terms, please contact us at:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CLINICAL_FONT_FAMILY,
                color: "#0B5FFF",
                mt: 0.5,
              }}
            >
              support@aiclintrack.com
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={onReject}
          disabled={loading}
          sx={{
            fontFamily: CLINICAL_FONT_FAMILY,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          I Disagree
        </Button>
        <Button
          onClick={onAccept}
          variant="contained"
          disabled={loading}
          sx={{
            fontFamily: CLINICAL_FONT_FAMILY,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          I Accept & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndConditionsModal;
